/**
 * Warranty Terms & Conditions overview page.
 *
 * Shows all warranty templates with their terms document status -
 * which have a linked PDF, which are missing.  Clicking a row
 * navigates to the template's Terms & Conditions tab.
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { FileText, AlertCircle, CheckCircle2 } from 'lucide-react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { Badge } from '@/shared/components/ui/badge'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'

export function WarrantyTermsPage() {
  const navigate = useNavigate()
  const { data: templates, isLoading, isFetching } = useWarrantyTemplates()

  // Templates missing their terms PDF sort first - that is the list a
  // config admin opens this page to work through.
  const rows = useMemo(() => {
    if (!templates) return []
    return templates
      .map((t) => {
        const termsDoc = (t.documents ?? []).find((d) => d.kind === 'TERMS')
        return {
          ...t,
          _termsDoc: termsDoc ?? null,
          _hasTerms: !!termsDoc,
          _termsLabel: termsDoc?.label ?? '',
        }
      })
      .sort((a, b) => Number(a._hasTerms) - Number(b._hasTerms))
  }, [templates])

  type Row = (typeof rows)[number]

  const columns: DataTableColumn<Row>[] = [
    {
      id: 'policy_code',
      header: 'Pub Number',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink)]">
          {row.policy_code || '-'}
        </span>
      ),
    },
    {
      id: 'name',
      header: 'Template Name',
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink)]">{row.name}</span>
      ),
    },
    {
      id: '_hasTerms',
      header: 'Terms Status',
      cell: (row) =>
        row._hasTerms ? (
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />
            <Badge variant="outline" className="text-[10px] text-[var(--green)]">
              Linked
            </Badge>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 text-[var(--status-warning)]" />
            <Badge variant="outline" className="text-[10px] text-[var(--status-warning)]">
              Missing
            </Badge>
          </div>
        ),
    },
    {
      id: '_termsLabel',
      header: 'Document',
      cell: (row) =>
        row._termsDoc ? (
          <div className="flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-[var(--ink-3)]" />
            <span className="truncate text-[12px] text-[var(--ink-2)]">
              {row._termsLabel}
            </span>
          </div>
        ) : (
          <span className="text-[12px] text-[var(--ink-3)]">
            No document
          </span>
        ),
    },
    {
      id: 'status',
      header: 'Template Status',
      cell: (row) => (
        <Badge variant="outline" className="text-[10px]">
          {row.status}
        </Badge>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <p className="text-[13px] text-[var(--ink-3)]">
        View which warranty templates have their terms PDF linked. Click a row to
        manage the template&apos;s terms document.
      </p>
      <DataTable
        tableId="warranty-terms-overview"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(row) => row.uuid}
        onRowClick={(row) => navigate(`/manage/warranties/templates/${row.uuid}`)}
      />
    </div>
  );
}
